<script>
	import { onMount, setContext } from 'svelte'
	import { getColors } from '../../colors'
	import MediaQuery, { breakpoint, isTouch, platform } from '../../components/MediaQuerySsr.svelte'
	import { getLanguage, translate } from '../../language'
	import { hexToRgb, px } from '../../utils'
	import Juxtapose from '../../components/Juxtapose.svelte'
	import InlineSVG from 'svelte-inline-svg'
	import { fade } from 'svelte/transition'

	import { hoverOutside } from '../../actions/hoverOutside.js'
	import { configCases } from './constants'

	import Minimap from '../../components/Minimap.svelte'
	import iconBuildings from '../../icons/buildings.svg'
	import iconTime from '../../icons/icon_destroyed_time.svg'
	import iconAttack from '../../icons/icon_attack.svg'
	import iconSlide from '../../icons/slide.svg'
	// import Minimap from '../../components/Minimap.svelte'
	import StaticAnnotatedMap from '../../components/StaticAnnotatedMap.svelte'
	import { writable } from 'svelte/store'

	// ///////////////////////////////////////////////////////////
	// init GMS graphic
	// ///////////////////////////////////////////////////////////
	export let graphicId
	export let options
	export let caseId

	const t = translate
	const colors = getColors()
	const language = getLanguage()

	options = { case_id: caseId, ...options }

	// ///////////////////////////////////////////////////////////
	// init, constants and definitions
	// ///////////////////////////////////////////////////////////
	let dataLoaded = false

	const isMobile = writable(undefined)
	setContext('isMobile', isMobile)
	$: $isMobile = $breakpoint === 'mobile-m' || $breakpoint === 'mobile-s'
	$: responsiveMode = $isMobile ? 'mobile' : 'desktop'

	const basePath = `${PATH}assets/01_destruction_before_after/${options.case_id}`

	const config = configCases[options.case_id]

	const imageWidth = 600
	// const imageHeightDesktop = 500
	// const imageHeightMobile = 600
	// $: imageHeight = $isMobile ? imageHeightMobile : imageHeightDesktop

	// ///////////////////////////////////////////////////////////
	// load data
	// ///////////////////////////////////////////////////////////

	onMount(async () => {
		dataLoaded = true
	})

	// ///////////////////////////////////////////////////////////
	// bound to components
	// ///////////////////////////////////////////////////////////
	let showSliderAnnotation = true

	let graphicWidth
	$: graphicHeight = (graphicWidth / 6) * ($isMobile ? 6 : 5)

	let positionSliderHorizontal
	let positionSliderVertical = 50
</script>

<MediaQuery />
<div
	class="tsg-01_destruction_before_after"
	class:is-touch={$isTouch}
	style:--background-color={colors.background}
	style:--background-color-raw={Object.values(hexToRgb(colors.background))}
	style:--primary-color={colors.primary}
	style:--text-color={colors.textBlack}
	style:--gray={colors.gray}
	bind:clientWidth={graphicWidth}
>
	<Juxtapose
		width={graphicWidth}
		height={graphicHeight}
		positionHorizontal={20}
		positionVertical={positionSliderVertical}
		bind:position={positionSliderHorizontal}
		on:start={() => (showSliderAnnotation = false)}
	>
		<!-- on:move={checkTextVisibility} -->

		<!-- SLIDER CONFIG -->
		<div class="slider-annotation" slot="annotation">
			{#if showSliderAnnotation}
				<!-- <div transition:fade>{t('Slide to explore the change')}</div> -->
				<div class="icon" transition:fade>
					<InlineSVG
						src={iconSlide}
						fill={colors.background}
						stroke={colors.textBlack}
						focusable="false"
					/>
				</div>
			{/if}
		</div>
		<div class="slider-handle" slot="slider">
			<div class="vertical-lines" />
		</div>
		<!-- LABELS -->
		<div class="slider-map-label" slot="label-left">{config.years[0]}</div>
		<div class="slider-map-label" slot="label-right">{config.years[1]}</div>
		<!-- LEFT -->
		<div
			slot="left"
			id="left-map"
			class="juxtapose-item"
			style:width={px(graphicWidth)}
			use:hoverOutside
			on:hover_outside={(e) => {
				if ($isTouch) return
			}}
		>
			<StaticAnnotatedMap
				maptype="aerial"
				imagePath="{basePath}/slider-{options.case_id}-before-{responsiveMode}-base-2x.min.png"
				vectorPath="{basePath}/slider-{options.case_id}-before-{responsiveMode}-highlights.svg"
				case_id={options.case_id}
				case_data={config}
				view={'before'}
				{imageWidth}
				width={graphicWidth}
			/>
		</div>
		<!-- RIGHT -->
		<div
			slot="right"
			id="right-map"
			class="juxtapose-item"
			style:width={px(graphicWidth)}
			use:hoverOutside
			on:hover_outside={(e) => {
				if ($isTouch) return
			}}
		>
			<StaticAnnotatedMap
				maptype="aerial"
				imagePath="{basePath}/slider-{options.case_id}-after-{responsiveMode}-base-2x.min.png"
				vectorPath="{basePath}/slider-{options.case_id}-after-{responsiveMode}-highlights.svg"
				case_id={options.case_id}
				case_data={config}
				view={'after'}
				{imageWidth}
				width={graphicWidth}
			/>
		</div>
	</Juxtapose>

	<!-- INFOBOX -->
	{#if dataLoaded}
		{@const { title, subtitle, description, details } = config}
		<div class="infobox">
			<div class="infobox__flex-container">
				<div class="text">
					<div class="title">{t(title)}</div>
					<div class="location">
						{t(subtitle)}
					</div>
					<div class="details">
						{#each details as detail}
							<div class="detail">
								<div class="icon">
									<InlineSVG
										class="inline-svg"
										src={detail?.startsWith('Destroy') ? iconTime : iconAttack}
										fill={colors.primary}
										focusable="false"
									/>
								</div>
								<div class="detail-label">{t(detail)}</div>
							</div>
						{/each}
					</div>
					<p class="description mobile-hide">
						{t(description)}
					</p>
				</div>
				<div class="minimap">
					<Minimap backgroundImagePath="{basePath}/minimap.min.png" />
				</div>
			</div>
			<p class="description mobile-show">
				{t(description)}
			</p>
		</div>
	{/if}
</div>

<style lang="scss">
	.tsg-01_destruction_before_after {
		user-select: none;
		position: relative;
		width: 100%;
		//max-width: 620px;
		margin: 0 auto;
		display: flex;
		gap: 1rem;
		flex-direction: column;
		// @media (max-width: $breakpoint-mobile-l) {
		// 	display: flex;
		// 	flex-direction: column-reverse;
		// }
	}

	// .vector-overlay {
	// 	position: absolute;
	// 	top: 0;
	// 	left: 0;
	// }

	.juxtapose-item {
		overflow: hidden;
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;

		&:not(.interactive) {
			pointer-events: none;
		}
	}

	.slider-annotation {
		font-weight: $bold;
		letter-spacing: 0.14px;
		font-size: 14px;
		color: var(--background-color);
		text-shadow:
			1px 1px 0 var(--text-color),
			-1px 1px 0 var(--text-color),
			-1px -1px 0 var(--text-color),
			1px -1px 0 var(--text-color);
		overflow-wrap: break-word;

		.icon {
			margin-top: 4px;
		}
	}

	.slider-handle {
		--thumb-size: 45px;
		width: var(--thumb-size);
		height: var(--thumb-size);
		background-color: var(--text-color);
		border: 2px solid var(--background-color);
		border-radius: 9999px;
		display: flex;
		align-items: center;
		justify-content: center;

		.vertical-lines {
			height: 50%;
			width: 6px;
			border-right: 1.5px solid var(--background-color);
			border-left: 1.5px solid var(--background-color);
			opacity: 0.5;
		}
	}

	.slider-map-label {
		font-weight: $bold;
		font-size: 17px;
		line-height: 120%; /* 20.4px */
		letter-spacing: 0.17px;
		padding: 0px 5px;
		background-color: var(--background-color);
		border-radius: 25px;
		width: fit-content;
		white-space: nowrap;
	}

	.icon {
		pointer-events: none;
	}

	:global(.translation-string) {
		fill: white;
		font-size: 14px;
	}

	.infobox {
		&__flex-container {
			display: flex;
			justify-content: space-between;
			padding: 15px 0px;
			gap: 15px;

			@media (max-width: $breakpoint-mobile-m) {
				align-items: center;
				padding: 7px 0px;
			}
		}

		b {
			font-weight: $bold;
		}

		.description {
			margin-top: 10px;
			line-height: 135%;
			opacity: 0.7;
			font-size: 14px;
		}

		.text {
			.title {
				font-size: 19px;
				font-weight: $bold;
				line-height: 120%; /* 22.8px */
				letter-spacing: 0.19px;

				@media (max-width: $breakpoint-mobile-l) {
					font-size: 17px;
					letter-spacing: 0.17px;
				}
			}

			.location {
				opacity: 0.7;
				font-size: 16px;
				letter-spacing: 0.16px;
				margin-bottom: 8px;

				@media (max-width: $breakpoint-mobile-l) {
					font-size: 14px;
					letter-spacing: 0.14px;
				}
			}

			.details {
				margin: 10px 0;

				@media (max-width: $breakpoint-mobile-m) {
					margin-bottom: 0;
				}
			}

			.detail {
				font-size: 14px;
				letter-spacing: 0.16px;
				margin: 5px 0;
				font-weight: 500;
				display: flex;
				align-items: center;
				gap: 3px;
				opacity: 0.7;

				&.detail--annotation {
					line-height: 125%;
				}

				.icon {
					width: 18px;
					height: 18px;
					display: inline-block;
					// vertical-align: text-top;
					margin-right: 2px;
					filter: brightness(0.8);

					// .inline-svg {
					// 	width: 100%;
					// 	height: 100%;
					// }
				}

				.detail-label {
					margin-bottom: -2px;
				}

				@media (max-width: $breakpoint-mobile-l) {
					font-size: 14px;
					letter-spacing: 0.13px;

					.icon {
						width: 13px;
						height: 13px;
					}

					:global(svg.inline-svg) {
						// margin-top: 4px;
					}
				}
			}
		}

		:global(svg.inline-svg) {
			width: 100%;
			min-width: 13px;
			height: auto;
			// margin-top: -2px;
		}

		.minimap {
			width: 110px;
			flex-shrink: 0;
		}
	}

	.mobile-hide {
		@media (max-width: $breakpoint-mobile-m) {
			display: none;
		}
	}

	.mobile-show {
		// FIXME: at 450px, there's ungovernmed state of 1px
		@media (min-width: calc($breakpoint-mobile-m + 1px)) {
			display: none;
		}
	}
</style>
